import type { Trade, ExecutionTradeOverlay } from "../types/trade";

const SL_COLOR = "#E00101";
const TP_COLOR = "#06E363";

export { SL_COLOR, TP_COLOR };

export function isExecutionTradeProfitable(t: ExecutionTradeOverlay): boolean {
  if (t.is_win) return true;
  if (t.is_loss) return false;
  if (t.net_pnl != null && Number.isFinite(Number(t.net_pnl))) {
    return Number(t.net_pnl) > 0;
  }
  if (t.entry_price != null && t.exit_price != null) {
    const entry = Number(t.entry_price);
    const exit = Number(t.exit_price);
    if (!Number.isFinite(entry) || !Number.isFinite(exit)) return false;
    return t.direction === "short" ? exit < entry : exit > entry;
  }
  return false;
}

export function isExecutionTradeLosing(t: ExecutionTradeOverlay): boolean {
  if (t.is_loss) return true;
  if (t.is_win) return false;
  if (t.net_pnl != null && Number.isFinite(Number(t.net_pnl))) {
    return Number(t.net_pnl) < 0;
  }
  if (t.entry_price != null && t.exit_price != null) {
    const entry = Number(t.entry_price);
    const exit = Number(t.exit_price);
    if (!Number.isFinite(entry) || !Number.isFinite(exit)) return false;
    return t.direction === "short" ? exit > entry : exit < entry;
  }
  return false;
}

function finitePrice(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function toIsoDateTime(value: Date | string | null | undefined): string | null {
  if (value == null) return null;
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Build chart overlay row from Trade Log snapshot when execution-candles returns none. */
export function tradeToExecutionOverlay(trade: Trade): ExecutionTradeOverlay {
  const net = trade.netPnL ?? trade.grossPnL ?? null;
  const netNum = net != null ? Number(net) : null;
  const isWin = netNum != null ? netNum > 0 : false;
  const isLoss = netNum != null ? netNum < 0 : false;

  return {
    trade_id: trade.id,
    symbol: trade.symbol,
    direction: trade.type === "sell" ? "short" : "long",
    entry_time: toIsoDateTime(trade.entryDate),
    exit_time: trade.status === "closed" ? toIsoDateTime(trade.exitDate) : null,
    entry_price: finitePrice(trade.entryPrice),
    exit_price: finitePrice(trade.exitPrice),
    tp_price: finitePrice(trade.takeProfit),
    sl_price: finitePrice(trade.stopLoss),
    entry_quantity: trade.entryQuantity ?? null,
    pips_to_tp: null,
    pips_to_sl: null,
    rr: null,
    is_win: isWin,
    is_loss: isLoss,
    net_pnl: netNum,
    close_kind: trade.positionSemantics?.close_kind ?? null,
    is_dust: Boolean(trade.positionSemantics?.is_dust),
  };
}

/** Merge trade-log TP/SL/exit onto execution-candles overlay when the API omitted them. */
export function enrichExecutionOverlayFromTrade(
  overlay: ExecutionTradeOverlay,
  trade?: Pick<
    Trade,
    "id" | "takeProfit" | "stopLoss" | "exitPrice" | "entryQuantity" | "positionSemantics"
  >
): ExecutionTradeOverlay {
  if (!trade) return overlay;
  return {
    ...overlay,
    trade_id: overlay.trade_id ?? trade.id,
    tp_price: overlay.tp_price ?? finitePrice(trade.takeProfit),
    sl_price: overlay.sl_price ?? finitePrice(trade.stopLoss),
    exit_price: overlay.exit_price ?? finitePrice(trade.exitPrice),
    entry_quantity: overlay.entry_quantity ?? trade.entryQuantity ?? null,
    close_kind: overlay.close_kind ?? trade.positionSemantics?.close_kind ?? null,
    is_dust: overlay.is_dust ?? Boolean(trade.positionSemantics?.is_dust),
  };
}

export type ResolvedOverlayLevels = {
  slPrice: number | null;
  tpPrice: number | null;
};

/** Stored SL/TP only — exit is drawn separately on the chart when missing. */
export function resolveOverlaySlTpLevels(t: ExecutionTradeOverlay): ResolvedOverlayLevels {
  return {
    slPrice: finitePrice(t.sl_price),
    tpPrice: finitePrice(t.tp_price),
  };
}

export type OverlayExitSegment = {
  price: number;
  color: string;
};

function resolveExitLineColor(
  t: ExecutionTradeOverlay & { exit_reason?: string | null }
): string {
  const reason = String(t.exit_reason ?? "").toLowerCase();
  if (reason === "sl_hit") return SL_COLOR;
  if (reason === "tp_hit") return TP_COLOR;

  const exit = finitePrice(t.exit_price);
  const sl = finitePrice(t.sl_price);
  const tp = finitePrice(t.tp_price);
  if (exit != null && sl != null && tp != null) {
    const toSl = Math.abs(exit - sl);
    const toTp = Math.abs(exit - tp);
    if (toSl <= toTp) return SL_COLOR;
    return TP_COLOR;
  }
  if (exit != null && sl != null && Math.abs(exit - sl) < 1e-4) return SL_COLOR;
  if (exit != null && tp != null && Math.abs(exit - tp) < 1e-4) return TP_COLOR;

  if (isExecutionTradeProfitable(t)) return TP_COLOR;
  if (isExecutionTradeLosing(t)) return SL_COLOR;
  return Number(t.net_pnl ?? 0) >= 0 ? TP_COLOR : SL_COLOR;
}

/** Actual exit line for simulation overlay — always at exit price, coloured like SL or TP. */
export function resolveActualExitOverlaySegment(
  t: ExecutionTradeOverlay & { exit_reason?: string | null }
): OverlayExitSegment | null {
  const exit = finitePrice(t.exit_price);
  if (exit == null || !t.exit_time) return null;
  return { price: exit, color: resolveExitLineColor(t) };
}

/**
 * Exit price line when SL/TP are absent: green (TP color) for wins, red (SL color) for losses.
 */
export function resolveOverlayExitSegment(
  t: ExecutionTradeOverlay & { exit_reason?: string | null }
): OverlayExitSegment | null {
  const sl = finitePrice(t.sl_price);
  const tp = finitePrice(t.tp_price);
  const exit = finitePrice(t.exit_price);
  if (exit == null) return null;
  if (sl != null && tp != null) return null;

  const profitable = isExecutionTradeProfitable(t);
  const losing = isExecutionTradeLosing(t);
  if (profitable && tp != null) return null;
  if (losing && sl != null) return null;

  return { price: exit, color: resolveExitLineColor(t) };
}

/** Up to 8 decimals for crypto; trims trailing zeros. */
export function formatCryptoAmount(value: number | null | undefined, maxDecimals = 8): string {
  if (value == null) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "0";

  const abs = Math.abs(n);
  let decimals = maxDecimals;
  if (abs >= 1000) decimals = 2;
  else if (abs >= 1) decimals = 4;

  let s = n.toFixed(decimals);
  if (s.includes(".")) {
    s = s.replace(/0+$/, "").replace(/\.$/, "");
  }
  return s.length > 0 ? s : "0";
}

/** Table meta: stored SL/TP only (exit price has its own column). */
export function formatOverlayTableSlTpLine(t: ExecutionTradeOverlay): string {
  const sl = finitePrice(t.sl_price);
  const tp = finitePrice(t.tp_price);
  return `SL ${sl != null ? formatCryptoAmount(sl) : "—"} / TP ${
    tp != null ? formatCryptoAmount(tp) : "—"
  }`;
}

export function formatOverlaySlTpAmount(value: number | null | undefined): string {
  const n = finitePrice(value);
  return n != null ? formatCryptoAmount(n) : "—";
}

/** True when baseline and sim values differ enough to show an arrow in the table. */
export function simulationTableValuesDiffer(
  baseline: number | null | undefined,
  simulated: number | null | undefined
): boolean {
  const a = finitePrice(baseline);
  const b = finitePrice(simulated);
  if (a == null || b == null) return false;
  return Math.abs(a - b) > 1e-6;
}

/**
 * SL/TP often have no baseline level (—) while sim fills one in.
 * Treat null→number as a visible sim change (unlike exit/PnL which always have both sides).
 */
export function simulationSlTpTableShouldShowSim(
  baseline: number | null | undefined,
  simulated: number | null | undefined
): boolean {
  const sim = finitePrice(simulated);
  if (sim == null) return false;
  const base = finitePrice(baseline);
  if (base == null) return true;
  return Math.abs(base - sim) > 1e-6;
}

export function overlayHasSimSlTp(t: ExecutionTradeOverlay): boolean {
  if (t.simulation_overlay !== "changed") return false;
  return (
    finitePrice(t.sim_sl_price) != null ||
    finitePrice(t.sim_tp_price) != null ||
    simulationSlTpTableShouldShowSim(t.sl_price, t.sim_sl_price) ||
    simulationSlTpTableShouldShowSim(t.tp_price, t.sim_tp_price)
  );
}
